// @ts-strict-ignore
import { Page, pickSeoProps } from '@components/Page'
import { RolexBreadcrumbs } from '@components/_rolex/Breadcrumbs'
import CtaButtons from '@components/_rolex/CtaButtons'
import { RolexImage } from '@components/_rolex/Image'
import Openinghours from '@components/_rolex/Openinghours'
import RolexPage from '@components/_rolex/Page'
import { RolexPushBanner } from '@components/_rolex/push-banner-grid/PushBanner'
import KeepExploring from '@components/_rolex/sliders/KeepExploring'
import StoreMap from '@components/_rolex/store-map'
import { Container } from '@components/gassan-ui'
import { Heading } from '@components/rolex-ui/Heading'
import { Text } from '@components/rolex-ui/Text'
import { SchemaOrg } from '@components/schema-org'
import { i18nTranslationNamespaces } from '@lib/i18n-translation-files'
import { SingleItemResponse, getCatchAllRolexStorePaths, getPageByCodename } from '@lib/kontent'
import { getPageByTypeAndUrlSlug } from '@lib/kontent/getPageByTypeAndUrlSlug'
import { RolexContactPage2979142 } from '@lib/kontent/models/RolexContactPage2979142'
import { RolexStorePageV72024 } from '@lib/kontent/models/RolexStorePageV72024'
import { GetStaticProps } from 'next'
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useRouter } from 'next/router'
import { FC } from 'react'

type Props = SingleItemResponse<RolexStorePageV72024> & {
  sid: string
  contactPageContent: SingleItemResponse<RolexContactPage2979142>
}

const StoreDetail: FC<Props> = ({ item, sid, contactPageContent }) => {
  const { t } = useTranslation('rolex')
  const { monday, tuesday, wednesday, thursday, friday, saturday, sunday } = item.elements
  const { locale } = useRouter()
  const openingHours = [
    monday.value,
    tuesday.value,
    wednesday.value,
    thursday.value,
    friday.value,
    saturday.value,
    sunday.value,
  ]
  const keepExploringItems = contactPageContent.item.elements.keep_exploring.value.map(
    (key) => contactPageContent.modular_content[key].elements,
  )
  return (
    <Page {...pickSeoProps(item.elements)} slug={`rolex/contact/${sid}`} noindex={locale === 'de'}>
      <SchemaOrg variant="rolex-store" data={item} />
      <RolexPage pageType="store contact page">
        <RolexBreadcrumbs
          items={[
            {
              label: t('navigation.contactUs', { ns: 'rolex' }),
              href: `/rolex/contact`,
            },
          ]}
          currentPageTitle={item.elements.name.value}
        />
        <Container className="flex !w-full grid-cols-[1fr_auto] flex-col-reverse md:grid md:items-center">
          <StoreMap lat={item.elements.latitude.value} lng={item.elements.longitude.value} />
          <div className="w-[35vw] min-w-[400px]">
            <div className="mx-auto w-[calc(100%-2rem)] py-10 md:w-full md:px-6 xl:px-[2.75rem]">
              <Text variant="legend16" className="mb-1 text-rolexBrown">
                {t('contact.officialRetailer')}
              </Text>
              <Heading variant="headline30" as="h2" className="mb-5 text-rolexBrown">
                {item.elements.name.value}
              </Heading>
              <Text variant="fixed16" weight="light" className="mb-5 whitespace-pre-line">
                {item.elements.address_details.value}
              </Text>
              <Openinghours
                timezone={
                  item.elements.country.value[0].codename === 'singapore'
                    ? 'Asia/Singapore'
                    : 'Europe/Amsterdam'
                }
                days={openingHours}
              />
              <CtaButtons
                phoneNumber={item.elements.phone_number.value}
                sendMessage
                directionsUrl={item.elements.google_maps_link.value}
              />
            </div>
          </div>
        </Container>
        <RolexImage
          mobileImage={item.elements.mobile_image}
          desktopImage={item.elements.desktop_image}
          size="xl"
          className="!mb-0"
        />
        <div className="bg-rolexLightBeige py-[60px] md:py-[90px]">
          <Container>
            <Heading variant="headline30" className="mb-4">
              {t('contactUs')}
            </Heading>
            <RolexPushBanner
              title={t('cta.message')}
              buttonLink={`/rolex/${t('rolex.sendAMessage', { ns: 'navigation' })}`}
              buttonText={t('contactUs')}
              mobileImage={contactPageContent.item.elements.contact_us_mobile_image}
              desktopImage={contactPageContent.item.elements.contact_us_desktop_image}
              gridLayout="n1_column"
            />
          </Container>
        </div>
        <KeepExploring className="py-[30px] md:py-[60px]" items={keepExploringItems} />
      </RolexPage>
    </Page>
  )
}

export const getStaticProps: GetStaticProps = async ({ locale, preview, params }) => {
  try {
    const sid = params.sid as string
    const language = ['sg', 'de'].includes(locale) ? 'en' : locale
    const content = await getPageByTypeAndUrlSlug<RolexStorePageV72024>({
      language,
      urlSlug: sid,
      type: 'rolex_store_page_v7_2024',
      preview,
    })
    const contactPageContent = await getPageByCodename<RolexContactPage2979142>({
      codename: 'rolex_contact_page',
      lang: language,
      preview,
    })
    return {
      props: {
        ...content,
        contactPageContent,
        sid,
        ...(await serverSideTranslations(language, i18nTranslationNamespaces)),
      },
    }
  } catch (error) {
    console.error(error)
    throw error
  }
}

export const getStaticPaths = async () => {
  const paths = await getCatchAllRolexStorePaths()

  return {
    fallback: false,
    paths,
  }
}

export default StoreDetail
